// Universal $apiFetch plugin: injects a fetch wrapper that automatically
// adds the active locale (x-locale) header on both server & client.
// Only locale is handled per user request.
import { VSF_LOCALE_COOKIE } from "~~/constants/core-constants";
import type { ResolvedFetchOptions, ResponseType } from "ofetch";

const getLocaleFromI18n = (locale: string | undefined, nuxtApp: Record<string, any>) => {

  const possibleI18n = (nuxtApp as unknown as { $i18n?: { locale?: unknown }}).$i18n;

  if (possibleI18n) {

    const localeFromI18n = (possibleI18n as { locale?: string | { value?: string }}).locale;

    if (typeof localeFromI18n === 'string') {

      locale = localeFromI18n;
    
    } else if (localeFromI18n && typeof localeFromI18n === 'object') {

      locale = (localeFromI18n as { value?: string }).value;
    
    }
  
  }

  return locale;

};

const getLocaleFromCookie = (locale: string | undefined) => {

  const getCookieLocale = () => {

    const cookieNameCandidates = [VSF_LOCALE_COOKIE, 'x-locale'];
    const source = import.meta.server
      ? (useRequestHeaders(['cookie'])['cookie'] || '')
      : (typeof document !== 'undefined' ? document.cookie : '');

    if (!source) {

      return undefined;

    }
    for (const cookieName of cookieNameCandidates) {

      const match = source.match(new RegExp('(?:^|; )' + cookieName + '=([^;]*)'));

      if (match) {

        return decodeURIComponent(match[1]);

      }
    
    }

    return undefined;
  
  };
  locale = getCookieLocale();

  return locale;

};

const getLocaleFromBrowser = (locale: string | undefined) => {

  if (import.meta.client && typeof navigator !== 'undefined') {

    locale = navigator.language;
  
  }

  return locale;

};

const getHeadersObject = (options: ResolvedFetchOptions<ResponseType, any>) => {

  const existing = options.headers as Headers | [string, string][] | Record<string, string> | undefined;
  let headersObj: Record<string, string> = {};

  if (existing instanceof Headers) {

    existing.forEach((v, k) => {

      headersObj[k] = v; 

    });
  
  } else if (Array.isArray(existing)) {

    for (const [k, v] of existing) {

      headersObj[k] = v;

    }
  
  } else if (typeof existing === 'object' && existing) {

    headersObj = { ...(existing as Record<string, string>) };
  
  }

  return headersObj;

};

const appendLocaleToHeaders = (options: ResolvedFetchOptions<ResponseType, any>, headersObj: Record<string, string>, locale: string) => {

  if (!headersObj['x-locale']) {

    headersObj['x-locale'] = locale;
  
  }
  (options as unknown as { headers: Record<string, string> }).headers = headersObj;

};

export default defineNuxtPlugin((nuxtApp) => {

  const apiFetch = $fetch.create({
    onRequest({ options }) {

      let locale: string | undefined;
      locale = getLocaleFromI18n(locale, nuxtApp);

      if (!locale) {

        locale = getLocaleFromCookie(locale);
      
      }

      if (!locale) {

        locale = getLocaleFromBrowser(locale);
      
      }

      if (!locale) {

        return;

      }

      const headersObj = getHeadersObject(options);
      appendLocaleToHeaders(options, headersObj, locale);
    
    },
  });

  return { provide: { apiFetch } };

});

declare module 'vue' {
  interface ComponentCustomProperties { $apiFetch: typeof $fetch; }
}